// src/components/layout/Navbar.tsx - Fixed dropdown z-index

'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useAuth } from '@/src/lib/auth/authContext';
import { User, ChevronDown } from 'lucide-react';

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const pathname = usePathname();
  const { user, logout } = useAuth();
  
  // Check if user is a Discord member with the role
  const isDiscordMember = user?.membershipType === 'discord' || user?.discordMemberRole === true;
  
  // Show membership tab condition:
  // - If user is not logged in
  // - If user is free or daily member
  // - Do not show for Discord members or monthly/weekend members
  const shouldShowMembership = !user || 
    ((user.membershipType === 'free' || user.membershipType === 'daily') && 
    !isDiscordMember);
  
  return (
    <nav className="bg-blue-900 text-white relative z-50">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <Link href="/" className="font-bold text-xl">
              Diamond AI
            </Link>
            <div className="hidden md:block ml-10">
              <div className="flex items-center space-x-4">
                <Link 
                  href="/" 
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    pathname === '/' ? 'bg-blue-800' : 'hover:bg-blue-800'
                  }`}
                >
                  ホーム
                </Link>
                <Link 
                  href="/races" 
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    pathname.startsWith('/races') ? 'bg-blue-800' : 'hover:bg-blue-800'
                  }`}
                >
                  レース一覧
                </Link>
                {shouldShowMembership && (
                  <Link 
                    href="/membership" 
                    className={`px-3 py-2 rounded-md text-sm font-medium ${
                      pathname === '/membership' ? 'bg-blue-800' : 'hover:bg-blue-800'
                    }`}
                  >
                    メンバーシップ
                  </Link>
                )}
              </div>
            </div>
          </div>
          <div className="hidden md:block">
            <div className="flex items-center ml-4">
              {user ? (
                <div className="relative">
                  <button 
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center px-3 py-2 rounded-md hover:bg-blue-800 transition-colors"
                  >
                    <User className="h-5 w-5 mr-1" />
                    <span className="mr-1">
                      {user.username || user.email}
                    </span>
                    {/* Badge for membership type */}
                    {isDiscordMember ? (
                      <span className="ml-2 bg-purple-600 text-white text-xs px-2 py-1 rounded-full">
                        Discord会員
                      </span>
                    ) : user.membershipType !== 'free' && (
                      <span className="ml-2 bg-green-600 text-white text-xs px-2 py-1 rounded-full">
                        {user.membershipType === 'monthly' ? '月額会員' :
                         user.membershipType === 'weekend' ? '週末会員' :
                         user.membershipType === 'daily' ? '1日会員' : ''}
                      </span>
                    )}
                    <ChevronDown className="ml-1 h-4 w-4" />
                  </button>
                  
                  {/* User menu dropdown - Added z-50 to ensure it stays on top */}
                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 text-gray-700 z-50">
                      <Link 
                        href="/dashboard" 
                        className="block px-4 py-2 text-sm hover:bg-gray-100"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        マイダッシュボード
                      </Link>
                      
                      {/* Show membership option for free/daily users only */}
                      {shouldShowMembership && (
                        <Link 
                          href="/membership" 
                          className="block px-4 py-2 text-sm hover:bg-gray-100"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          メンバーシップ購入
                        </Link>
                      )}
                      
                      {/* Add link to Whop for Discord members */}
                      {isDiscordMember && (
                        <a 
                          href="https://whop.com/hub/memberships" 
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block px-4 py-2 text-sm hover:bg-gray-100"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          支払い設定（Whop）
                        </a>
                      )}
                      
                      <button
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          logout();
                        }}
                        className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        ログアウト
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <Link 
                    href="/login" 
                    className="bg-transparent hover:bg-blue-700 text-white font-medium py-2 px-4 rounded mr-2"
                  >
                    ログイン
                  </Link>
                  <Link 
                    href="/register" 
                    className="bg-white text-blue-900 hover:bg-gray-200 font-medium py-2 px-4 rounded"
                  >
                    会員登録
                  </Link>
                </>
              )}
            </div>
          </div>
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="bg-blue-800 inline-flex items-center justify-center p-2 rounded-md text-white hover:bg-blue-700 focus:outline-none"
            >
              <svg 
                className="h-6 w-6" 
                xmlns="http://www.w3.org/2000/svg" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile menu - Added z-40 to ensure proper layering */}
      {isMenuOpen && (
        <div className="md:hidden relative z-40">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <Link 
              href="/" 
              className={`block px-3 py-2 rounded-md text-base font-medium ${
                pathname === '/' ? 'bg-blue-800' : 'hover:bg-blue-800'
              }`}
            >
              ホーム
            </Link>
            <Link 
              href="/races" 
              className={`block px-3 py-2 rounded-md text-base font-medium ${
                pathname.startsWith('/races') ? 'bg-blue-800' : 'hover:bg-blue-800'
              }`}
            >
              レース一覧
            </Link>
            {shouldShowMembership && (
              <Link 
                href="/membership" 
                className={`block px-3 py-2 rounded-md text-base font-medium ${
                  pathname === '/membership' ? 'bg-blue-800' : 'hover:bg-blue-800'
                }`}
              >
                メンバーシップ
              </Link>
            )}
            
            {user ? (
              <>
                <Link
                  href="/dashboard"
                  className="block px-3 py-2 rounded-md text-base font-medium hover:bg-blue-800"
                >
                  マイダッシュボード
                </Link>
                
                {/* Discord-specific link for mobile */}
                {isDiscordMember && (
                  <a
                    href="https://whop.com/hub/memberships"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-3 py-2 rounded-md text-base font-medium hover:bg-blue-800"
                  >
                    支払い設定（Whop）
                  </a>
                )}
                
                <div className="px-3 py-2 text-base font-medium text-gray-300">
                  {user.username || user.email}
                  {isDiscordMember ? (
                    <span className="ml-2 bg-purple-600 text-white text-xs px-2 py-1 rounded-full">
                      Discord会員
                    </span>
                  ) : user.membershipType !== 'free' && (
                    <span className="ml-2 bg-green-600 text-white text-xs px-2 py-1 rounded-full">
                      {user.membershipType === 'monthly' ? '月額会員' :
                       user.membershipType === 'weekend' ? '週末会員' :
                       user.membershipType === 'daily' ? '1日会員' : ''}
                    </span>
                  )}
                </div>
                <button
                  onClick={logout}
                  className="block w-full text-left px-3 py-2 rounded-md text-base font-medium hover:bg-blue-800"
                >
                  ログアウト
                </button>
              </>
            ) : (
              <>
                <Link 
                  href="/login" 
                  className="block px-3 py-2 rounded-md text-base font-medium hover:bg-blue-800"
                >
                  ログイン
                </Link>
                <Link 
                  href="/register" 
                  className="block px-3 py-2 rounded-md text-base font-medium hover:bg-blue-800"
                >
                  会員登録
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
